import { Flex, Stack } from '@chakra-ui/react';
import ParentSize from '@visx/responsive/lib/components/ParentSize';
import Image from 'next/image';
import { useMemo } from 'react';
import { Heading, Paragraph } from 'ui-components';
import { Creator } from '~/clientApi/creator.model';
import { Platform } from '~/screens/AddTalent/constants';
import { CountryMap } from '~/screens/CreatorAudienceDemographics/CountryDistribution';
import { useInsights } from '~/screens/CreatorAudienceDemographics/GenderDistribution';

import PlaceholderCountries from '../static/countries.png';
import { CountriesDistributionSTMComponent } from './CountriesDistributionSTMComponent';

export type DistributionProps = {
  insightId: string;
  platform: Platform;
  isConnected: boolean;
  isPredictedVisible?: boolean;
  creator?: Creator;
};

export const CountryDistribution = ({
  insightId,
  platform,
  isConnected,
  isPredictedVisible = false,
  creator,
}: DistributionProps) => {
  const insights = useInsights({ insightId, platform });

  const countries = useMemo(() => {
    const top3 = insights?.data?.countries
      ?.sort((c1, c2) => {
        // API doesn't sort this array :\
        if (c1.value < c2.value) return 1;
        if (c1.value > c2.value) return -1;
        return 0;
      })
      .slice(0, 3);

    return (
      top3?.map((c) => ({
        country: c.meta?.countryName || c.label,
        value: Math.round(c.value * 100),
      })) || []
    );
  }, [insights?.data]);

  const countryMap = useMemo(() => {
    return (
      <>
        <Heading size="sm">Country distribution</Heading>
        <ParentSize>
          {({ width }) => (
            <CountryMap width={width} height={185} data={countries} />
          )}
        </ParentSize>
        <Stack>
          {countries.map((c) => (
            <Flex gap={2} alignItems="center" key={c.country}>
              <Paragraph fontWeight="medium">{c.value}%</Paragraph>
              <span>{c.country}</span>
            </Flex>
          ))}
        </Stack>
      </>
    );
  }, [countries]);

  if (isPredictedVisible) {
    return (
      <CountriesDistributionSTMComponent
        creator={creator}
        countryMap={countryMap}
      />
    );
  }

  // This is the previous implementation; it will render if STMPrediction is not available
  if (!isConnected && !isPredictedVisible) {
    return <Image src={PlaceholderCountries} alt="countries distribution" />;
  }

  return countryMap;
};
